import { DataGrid, DataGridProps } from '@mui/x-data-grid';
import { Alert, Stack } from '@mui/material';
import {
  BaseFlexibleReportRequest,
  GetFlexibleReportRequestsParams,
  useGetFlexibleReportRequestsQuery,
} from '@features/flexible-reports';
import {
  useAllCurrentReports,
  useCurrentCampaign,
} from '@features/campaigns/subdomains/visitation/hooks';
import { skipToken } from '@reduxjs/toolkit/query';
import { columns } from './columns';
import { memo, useState } from 'react';
import { debounce } from 'lodash';
import { unifiedPlatformColorPalette } from '@utils/unifiedPlatformColorPalette';
import Loading from '@components/shared/Loading';
import { ActionBar } from './ActionBar';
import { Temporal } from 'temporal-polyfill';
import { getMinAllowedDate } from '../../../../../flexible-reports/domain/dates';
import { useFeatures } from '@utils/hooks';

const pageSizeOptions = [10, 25, 50];
const POLLING_INTERVAL = 60000;

const flexibleReportFieldToSortKeyParam: Record<
  keyof Pick<
    BaseFlexibleReportRequest,
    'name' | 'startDate' | 'endDate' | 'locationCount' | 'createdAt' | 'status'
  >,
  NonNullable<GetFlexibleReportRequestsParams['sort_key']>
> = {
  name: 'name',
  startDate: 'start_date',
  endDate: 'end_date',
  locationCount: 'location_count',
  createdAt: 'created_at',
  status: 'status',
};

type SortableField = keyof typeof flexibleReportFieldToSortKeyParam;

const DEFAULT_SORT_KEY: SortableField = 'createdAt';
const DEFAULT_SORT_ORDER: GetFlexibleReportRequestsParams['sort_direction'] =
  'desc';

export const FlexibleReports = memo(() => {
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(pageSizeOptions[0]);
  const [sortKey, setSortKey] = useState<SortableField>(DEFAULT_SORT_KEY);
  const [sortOrder, setSortOrder] =
    useState<GetFlexibleReportRequestsParams['sort_direction']>(
      DEFAULT_SORT_ORDER
    );
  const [searchText, setSearchText] = useState('');
  const { campaign, isFetching: isFetchingCampaign } = useCurrentCampaign();
  const { data, isFetching } = useGetFlexibleReportRequestsQuery(
    campaign?.id
      ? {
          campaignId: campaign.id,
          page: page + 1,
          per_page: pageSize,
          search_text: searchText,
          sort_key: flexibleReportFieldToSortKeyParam[sortKey],
          sort_direction: sortOrder,
        }
      : skipToken,
    { pollingInterval: POLLING_INTERVAL }
  );
  const { reports, loading: loadingReports } = useAllCurrentReports();
  const { flexibleReportDateValidation } = useFeatures();

  if (isFetchingCampaign || loadingReports) return <Loading />;

  if (!campaign) return <Alert severity="error">No campaign found</Alert>;

  const hasApprovedReports = reports.some(
    report => report.status === 'approved'
  );

  if (!hasApprovedReports)
    return <Alert severity="info">No approved reports found</Alert>;

  const debouncedSearch = debounce((value: string) => {
    setSearchText(value);
  }, 500);

  const onSortModelChange: DataGridProps['onSortModelChange'] = model => {
    const sortItem = model[0];
    const newSortField = (sortItem?.field as SortableField) ?? sortKey;
    const newSortOrder: GetFlexibleReportRequestsParams['sort_direction'] =
      sortItem?.sort ?? 'asc';
    setPage(0);
    setSortOrder(newSortOrder);
    setSortKey(newSortField);
  };

  const endDate = Temporal.PlainDate.from(campaign.endDate);
  const minAllowedDate = getMinAllowedDate();

  const disableNewReport =
    flexibleReportDateValidation &&
    Temporal.PlainDate.compare(endDate, minAllowedDate) < 0;

  return (
    <Stack direction="column" gap="1rem">
      <ActionBar
        campaignId={campaign.id}
        searchText={searchText}
        onSearch={debouncedSearch}
        disableNewReport={disableNewReport}
      />

      <DataGrid
        disableRowSelectionOnClick
        columns={columns}
        rows={data?.data.flexibleReportRequests ?? []}
        loading={isFetching}
        paginationMode="server"
        paginationModel={{ page, pageSize }}
        rowCount={data?.data.total ?? 0}
        pageSizeOptions={pageSizeOptions}
        onPaginationModelChange={({ page, pageSize }) => {
          setPage(page);
          setPageSize(pageSize);
        }}
        sortingMode="server"
        onSortModelChange={onSortModelChange}
        sortModel={[{ sort: sortOrder, field: sortKey ?? DEFAULT_SORT_KEY }]}
        sx={{
          height: 'auto',
          backgroundColor: 'white',
          padding: '1rem',
          a: {
            color: unifiedPlatformColorPalette.inmarketGreen,
            textDecoration: 'underline',
          },
          'a:hover': {
            color: unifiedPlatformColorPalette.inmarketGreen,
          },
          '& .MuiDataGrid-actionsCell': {
            opacity: 0,
          },
          [`& .MuiDataGrid-row:hover, & .MuiDataGrid-row:focus-within`]: {
            '.MuiDataGrid-actionsCell': {
              opacity: 1,
            },
          },
        }}
      />
    </Stack>
  );
});

FlexibleReports.displayName = 'FlexibleReports';
